import type { DocumentAnnotationSet } from "../../../../api/content/documentAnnotationsApi";
import { itemIdOf } from "../../../../api/content/processDefinitions";
import type { AcceptanceStep, CurationState } from "../../../../api/knowledgeData/knowledgeDataDecoders";
import type { DocumentResult } from "../../../../api/knowledgeData/lensDecoders";
import type { AnnotationSet } from "../../../../api/threads/liveMessages";

/**
 * What was added to a document (F84 D-02, §9), from its two owners: module B's content
 * annotation sets and module D's extraction results. Annotations come first, then
 * statements, each in its state. A merged content set reads as accepted (provisional,
 * documents-and-lenses P-1): both mean it is part of the project's data.
 */

/** Where an added item opens: a result's page, a proposed set's review, a merged set's item page. */
export type AddedTarget =
  | { readonly kind: "result"; readonly source: string; readonly review: AcceptanceStep | null }
  | { readonly kind: "review"; readonly changeSetId: string; readonly origin: string | null }
  | { readonly kind: "item"; readonly reference: string; readonly name: string };

export interface AddedAnnotations {
  /** The version shown, as chat references it. */
  readonly key: string;
  /** The same set across its versions, so a refresh that brings a newer one keeps its place and an editor open on it (Sol S4). */
  readonly identity: string;
  readonly state: CurationState;
  readonly set: AnnotationSet;
  readonly target: AddedTarget;
}

export interface AddedStatements {
  readonly source: string;
  readonly state: CurationState;
  /** Its acceptance step, once one was asked for it. */
  readonly review: AcceptanceStep | null;
  readonly statements: number;
  readonly document: string;
  readonly recordedAt: string;
}

export interface AddedToDocument {
  readonly annotations: readonly AddedAnnotations[];
  readonly statements: readonly AddedStatements[];
}

/** Shown without asking: what is part of the project's data. */
export function isShownFirst(state: CurationState): boolean {
  return state === "accepted";
}

export function addedToDocument(sets: readonly DocumentAnnotationSet[], results: readonly DocumentResult[]): AddedToDocument {
  const content = sets.map((set): AddedAnnotations => ({
    key: set.reference,
    identity: set.changeSetId === null ? itemIdOf(set.reference) : `${set.changeSetId}/${itemIdOf(set.reference)}`,
    state: set.state === "merged" ? "accepted" : "proposed",
    set: set.annotationSet,
    target: set.changeSetId === null ? { kind: "item", reference: set.reference, name: set.name } : { kind: "review", changeSetId: set.changeSetId, origin: set.origin[0] ?? null },
  }));
  const recorded = results.flatMap((result): AddedAnnotations[] => result.annotationSet === null ? [] : [{
    key: result.source,
    identity: result.source,
    state: result.state,
    set: result.annotationSet,
    target: { kind: "result", source: result.source, review: result.review },
  }]);
  return {
    annotations: byState([...content, ...recorded]),
    statements: byState(results.filter((result) => result.kind === "instance_data").map((result) => ({
      source: result.source,
      state: result.state,
      review: result.review,
      statements: result.statements,
      document: result.document,
      recordedAt: result.recordedAt,
    }))),
  };
}

/** Accepted first; otherwise the owners' order (newest first) is kept. */
function byState<T extends { readonly state: CurationState }>(items: readonly T[]): T[] {
  return [...items.filter((item) => isShownFirst(item.state)), ...items.filter((item) => !isShownFirst(item.state))];
}
